/**
 * AP-31 Nachtrag — **Vorgaben für Geschäftspartner.**
 *
 * Umsatzsteuer, Zahlungsbedingungen je Rolle, Belegwährung und Belegsprache,
 * mit denen ein **neu angelegter** Partner vorbelegt wird — dazu die eigenen
 * Adressarten des Mandanten.
 *
 * Kopiert, nicht verwiesen: Wer die Vorgabe ändert, ändert keinen bestehenden
 * Partner. Das steht in der Maske, damit niemand eine Massenänderung erwartet.
 *
 * AP-36 Phase D: feldweise ohne Knopf, auf der Stelle `tenant` der Seite. Der
 * Server führt `partnerDefaults` und die Bedingungen einer Rolle je einen
 * Schlüssel tief zusammen (S-43); `null` löscht eine Vorgabe. Zahlungsziel und
 * Skonto einer Rolle sind nur zusammen gültig und bilden eine Gruppe.
 */

import { useTranslation } from 'react-i18next';
import { CURRENCIES } from '@symphenia/shared-types';
import {
  Abschnitt,
  FeldStatus,
  FeldZeile,
  Feldgruppe,
  SchalterZeile,
  feldKlasse,
  useFeld,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern';

const P = 'partnerDefaults';

export function PartnerDefaultsSection({ stelle }: { stelle: Speicherstelle<unknown> }) {
  const { t } = useTranslation();
  const steuer = useFeld(stelle, `${P}.taxScheme`, { art: 'auswahl', label: t('clients.payment.taxScheme') });
  const waehrung = useFeld(stelle, `${P}.currency`, { art: 'auswahl', label: t('clients.payment.currency') });
  const sprache = useFeld(stelle, `${P}.language`, { art: 'auswahl', label: t('clients.payment.language') });
  const ansprache = useFeld(stelle, `${P}.contactFormOfAddress`, {
    art: 'auswahl',
    label: t('tenants.partnerDefaults.contactFormOfAddress'),
  });
  const externeId = useFeld<boolean>(stelle, `${P}.requireExternalId`, {
    art: 'schalter',
    label: t('tenants.partnerDefaults.requireExternalId'),
  });
  const bezeichnungen = useFeld<string[]>(stelle, 'partnerAddressLabels', {
    label: t('tenants.partnerDefaults.addressLabels'),
    // Eine Zeile je Adressart; leere Zeilen fallen weg.
    lies: (text) =>
      text
        .split('\n')
        .map((z) => z.trim())
        .filter(Boolean),
    schreib: (w) => (Array.isArray(w) ? w.join('\n') : ''),
  });

  return (
    <Abschnitt titel={t('tenants.partnerDefaults.title')}>
      <p className="text-sm text-muted-foreground">{t('tenants.partnerDefaults.intro')}</p>

      <FeldZeile label={t('clients.payment.taxScheme')} status={steuer}>
        <select className={feldKlasse} {...steuer.props}>
          <option value="">{t('tenants.partnerDefaults.none')}</option>
          <option value="STANDARD">{t('clients.taxSchemes.STANDARD')}</option>
          <option value="REVERSE_CHARGE">{t('clients.taxSchemes.REVERSE_CHARGE')}</option>
          <option value="SMALL_BUSINESS">{t('clients.taxSchemes.SMALL_BUSINESS')}</option>
          <option value="EXEMPT">{t('clients.taxSchemes.EXEMPT')}</option>
        </select>
      </FeldZeile>
      <FeldZeile label={t('clients.payment.currency')} status={waehrung}>
        <select className={feldKlasse} {...waehrung.props}>
          <option value="">{t('tenants.partnerDefaults.none')}</option>
          {CURRENCIES.map((w) => (
            <option key={w.code} value={w.code}>
              {w.code} — {w.de}
            </option>
          ))}
        </select>
      </FeldZeile>
      <FeldZeile label={t('clients.payment.language')} status={sprache}>
        <select className={feldKlasse} {...sprache.props}>
          <option value="">{t('tenants.partnerDefaults.none')}</option>
          <option value="de">Deutsch</option>
          <option value="en">English</option>
        </select>
      </FeldZeile>

      <FeldZeile
        label={t('tenants.partnerDefaults.contactFormOfAddress')}
        status={ansprache}
        hilfe={t('tenants.partnerDefaults.contactFormOfAddressHint')}
      >
        <span className="flex items-center gap-4 py-1">
          {(['FORMAL', 'INFORMAL'] as const).map((a) => (
            <span key={a} className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="radio"
                name="contactFormOfAddress"
                checked={(ansprache.wert ?? 'FORMAL') === a}
                onChange={() => ansprache.setze(a)}
              />
              {t(`clients.formsOfAddress.${a}`)}
            </span>
          ))}
        </span>
      </FeldZeile>

      <SchalterZeile
        feld={externeId}
        label={t('tenants.partnerDefaults.requireExternalId')}
        hinweis={t('tenants.partnerDefaults.requireExternalIdHint')}
      />

      <Bedingungen stelle={stelle} rolle="customer" titel={t('clients.payment.asCustomer')} />
      <Bedingungen stelle={stelle} rolle="supplier" titel={t('clients.payment.asSupplier')} />

      <FeldZeile
        label={t('tenants.partnerDefaults.addressLabels')}
        status={bezeichnungen}
        hilfe={t('tenants.partnerDefaults.addressLabelsHint')}
      >
        <textarea rows={3} className={`${feldKlasse} resize-y`} {...bezeichnungen.props} />
      </FeldZeile>
    </Abschnitt>
  );
}

const zahl = (w: unknown) => (typeof w === 'number' ? w : null);

/** Zahlungsbedingungen einer Rolle — eine Gruppe: Skonto nur vor dem Zahlungsziel. */
function Bedingungen({ stelle, rolle, titel }: { stelle: Speicherstelle<unknown>; rolle: 'customer' | 'supplier'; titel: string }) {
  const { t } = useTranslation();
  const pfad = `${P}.${rolle}`;
  const pruefe = (werte: Datensatz) => {
    const ziel = zahl(werte[`${pfad}.paymentTermsDays`]);
    const skonto = zahl(werte[`${pfad}.discountDays`]);
    return ziel !== null && skonto !== null && skonto >= ziel ? t('clients.discountBeforePaymentTerms') : null;
  };
  return (
    <Feldgruppe stelle={stelle} pruefe={pruefe} label={titel} className="rounded-md border border-border px-3 pb-3 pt-2">
      <p className="mb-2 text-sm font-semibold text-foreground">{titel}</p>
      <BedingungsFelder stelle={stelle} pfad={pfad} />
    </Feldgruppe>
  );
}

function BedingungsFelder({ stelle, pfad }: { stelle: Speicherstelle<unknown>; pfad: string }) {
  const { t } = useTranslation();
  const tage = (w: number | null) => (w !== null && (!Number.isInteger(w) || w < 0 || w > 365) ? '0 bis 365 Tage' : null);
  const ziel = useFeld<number | null>(stelle, `${pfad}.paymentTermsDays`, { art: 'zahl', label: t('clients.payment.paymentTermsDays'), pruefe: tage });
  const skontoTage = useFeld<number | null>(stelle, `${pfad}.discountDays`, { art: 'zahl', label: t('clients.payment.discountDays'), pruefe: tage });
  const skonto = useFeld<number | null>(stelle, `${pfad}.discountPercent`, {
    art: 'zahl',
    stellen: 2,
    label: t('clients.payment.discountPercent'),
    pruefe: (w) => (w !== null && (w < 0 || w > 100) ? '0 bis 100 %' : null),
  });
  const zahlungsart = useFeld(stelle, `${pfad}.paymentMethod`, { art: 'auswahl', label: t('clients.payment.paymentMethod') });

  return (
    <div className="grid gap-3 sm:grid-cols-4">
      {[
        { feld: ziel, label: t('clients.payment.paymentTermsDays') },
        { feld: skontoTage, label: t('clients.payment.discountDays') },
        { feld: skonto, label: t('clients.payment.discountPercent') },
      ].map(({ feld, label }) => (
        <label key={feld.props.name} className="block text-sm">
          <span className="mb-1 block text-muted-foreground">{label}</span>
          <input inputMode="decimal" className={feldKlasse} {...feld.props} />
          <FeldStatus status={feld} />
        </label>
      ))}
      <label className="block text-sm">
        <span className="mb-1 block text-muted-foreground">{t('clients.payment.paymentMethod')}</span>
        <select className={feldKlasse} {...zahlungsart.props}>
          <option value="">{t('tenants.partnerDefaults.none')}</option>
          <option value="TRANSFER">{t('clients.paymentMethods.TRANSFER')}</option>
          <option value="DIRECT_DEBIT">{t('clients.paymentMethods.DIRECT_DEBIT')}</option>
          <option value="CASH">{t('clients.paymentMethods.CASH')}</option>
          <option value="CARD">{t('clients.paymentMethods.CARD')}</option>
        </select>
        <FeldStatus status={zahlungsart} />
      </label>
    </div>
  );
}
